import { useRef, useState } from 'react';
import type { AppDownloadBannerProps } from '../components/AppDownloadBanner';
import { analyticsClient, trackEvent } from '../utils/analytics';
import { devicePlatform, isInNativeApp } from '../utils/deviceUtils';
import { STORE_LINKS } from '../constants/storeLinks';

const DISMISSED_AT_KEY = 'app-banner-dismissed-at';
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
// L'Oréal staff have their own L'Oréal Luxe / DPGP apps: gps-app is not for them — except
// the Flagship desk, which uses no dedicated app.
const LOREAL_EMAIL = /@loreal\.com$/i;
const FLAGSHIP_SERVICE_DESK_ID = 403;

interface AppDownloadBannerInput {
  pathname: string;
  email: string | null;
  serviceDeskId: number | null;
  hasMobileApp: boolean;
  isImpersonating: boolean;
  role: string;
  serviceDeskName: string;
}

const isDismissed = (): boolean => {
  try {
    return Date.now() - Number(localStorage.getItem(DISMISSED_AT_KEY)) < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
};

const persistDismissal = (): void => {
  try {
    localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
  } catch {
    // ponytail: blocked storage, the banner stays hidden for this session only
  }
};

const track = (eventName: string, props: Record<string, string>): void => {
  try {
    trackEvent(eventName, { props });
  } catch {
    // An adblocker must never break the link
  }
};

export const useAppDownloadBanner = ({
  pathname,
  email,
  serviceDeskId,
  hasMobileApp,
  isImpersonating,
  role,
  serviceDeskName,
}: AppDownloadBannerInput): AppDownloadBannerProps | null => {
  const [dismissed, setDismissed] = useState(isDismissed);
  const openedRef = useRef(false);
  const platform = devicePlatform();
  const isTaskRoute = pathname.startsWith('/request') || pathname.includes('/forms/');
  const usesLorealApp = LOREAL_EMAIL.test((email ?? '').trim()) && serviceDeskId !== FLAGSHIP_SERVICE_DESK_ID;

  if (platform === 'desktop' || dismissed || hasMobileApp || isImpersonating || isTaskRoute || usesLorealApp || isInNativeApp()) return null;

  const props = { platform, role, client: analyticsClient(serviceDeskName) };

  const hide = (eventName: string): void => {
    track(eventName, props);
    persistDismissal();
  };

  return {
    url: STORE_LINKS[platform],
    onOpen: (event) => {
      if (openedRef.current) {
        event.preventDefault();
        return;
      }
      openedRef.current = true;
      hide('App Download Click');
      // Deferred so the anchor is still mounted when the browser follows it
      setTimeout(() => setDismissed(true), 0);
    },
    onDismiss: () => {
      // Already counted and persisted by the open, whose deferred hide has not run yet
      if (!openedRef.current) hide('App Download Dismiss');
      setDismissed(true);
    },
  };
};
